<template>
  <main class="mx-auto">
    <template v-if="isLoading">
      <div class="flex flex-col gap-6 py-36 px-5 container mx-auto">
        <UtilsSkeletonLoading class="w-full lg:w-3/4 h-36" />
        <UtilsSkeletonLoading class="w-[90%] lg:w-1/2 h-24" />
      </div>
    </template>
    <CommonBanner
      v-else
      :data="industryDetail"
      :in-detail="true"
      :light-theme="true"
      text-to="Industries"
      url="/industries"
      :style="{
        'background-image': `url(${bgImage})`
      }"
      :half-width="true"
    />

    <section
      v-if="
        industryDetail?.attributes?.headline_challenge ||
        industryDetail?.attributes?.challenge
      "
      class="container mx-auto py-6 md:py-10"
    >
      <h2 class="section-heading mb-12">
        {{ industryDetail?.attributes?.headline_challenge || '' }}
      </h2>
      <p
        class="text-sm sm:text-base text-bw-900 w-full md:w-3/4 mb-5 whitespace-pre-line"
      >
        {{ industryDetail?.attributes?.challenge || '' }}
      </p>
    </section>

    <section class="container mx-auto py-6 md:py-10">
      <h2 class="section-heading mb-8 text-center md:text-left">
        {{
          industryDetail.attributes?.headline_service ||
          `How Suitmedia Deliver Value to ${industryDetail.attributes?.title} Sector`
        }}
      </h2>
      <div class="grid grid-cols-1 md:grid-cols-2 gap-y-10 gap-x-14">
        <template v-if="isLoading">
          <div v-for="i in 2" :key="i" class="flex">
            <UtilsSkeletonLoading class="w-6 h-7" />
            <div class="ml-4 flex-1">
              <UtilsSkeletonLoading class="w-full mb-2 h-7" />
              <UtilsSkeletonLoading class="w-full h-24" />
            </div>
          </div>
        </template>
        <template v-else>
          <div
            v-for="challenge in industryDetail.attributes.challenges"
            :key="challenge.id"
            class="flex items-start gap-y-8"
          >
            <div class="icon w-10">
              <NuxtImg
                format="webp"
                srcset=""
                :src="challenge.icon?.data?.attributes?.url || '/'"
                :alt="challenge.title"
                :title="challenge.title"
                loading="lazy"
                class="w-full h-full"
              />
            </div>
            <div class="ml-4 flex-1">
              <h3 class="font-medium text-lg mb-2">
                {{ challenge.title }}
              </h3>
              <p class="text-base">
                {{ challenge.description }}
              </p>
            </div>
          </div>
        </template>
      </div>
    </section>

    <section
      v-if="industryDetail.attributes?.approach"
      class="container mx-auto py-6 md:pt-10 md:pb-10 w-[90%] md:w-full"
    >
      <div
        class="content w-full md:w-3/4"
        v-html="industryDetail.attributes?.approach"
      ></div>
    </section>

    <WorkIndustryClients
      :industry-id="industryDetail.id"
      :industry-title="
        industryDetail.attributes?.headline_client ||
        `Our Trusted Partners in the ${industryDetail.attributes?.title} Sector`
      "
    />

    <div class="container mx-auto">
      <WorkCaseStudiesList
        :header-text="
          industryDetail.attributes?.headline_work ||
          `Transforming ${industryDetail.attributes?.title} Sector with Impactful Digital Solutions`
        "
        :filter-industry="industryDetail.attributes?.title"
      />

      <CommonFAQ
        :title="
          industryDetail?.attributes?.headline_faq ||
          `Frequently Asked Questions About ${industryDetail?.attributes?.title}`
        "
        type="industry"
        :component-id="industryDetail?.id"
      />

      <WorkArticlesList
        :header-text="
          industryDetail.attributes?.headline_article ||
          `Expert Insights and Digital Trends for the ${industryDetail.attributes?.title} Sector`
        "
        :filter-industry="industryDetail.attributes?.title"
      />
    </div>
  </main>
</template>

<script setup>
import { useGlobalStore } from '~/store/global'

const global = useGlobalStore()
const mainSeo = global.mainSeo

const route = useRoute()
const isLoading = ref(true)

const industryDetail = ref({})

const getIndustryDetail = async () => {
  const { data, error, pending } = await useSuitmediaApiData(
    `/api/industries/${route.params.slug}`,
    {
      query: {
        'fields[0]': 'id',
        'fields[1]': 'title',
        populate:
          'banner.image,banner.background_image,challenges.icon,seo.image'
      }
    }
  )

  isLoading.value = pending.value

  if (error.value) {
    throw createError({
      statusCode: 404,
      message: `Industry does not exist`
    })
  }

  return data.value
}

const industries = await getIndustryDetail()
industryDetail.value = industries.data

useJsonld({
  '@context': 'https://schema.org/',
  '@type': 'Organization',
  name: industryDetail?.value?.attributes?.seo?.seo_title || 'Suitmedia',
  url: industryDetail?.value?.attributes?.url || 'suitmedia.com',
  description:
    industryDetail?.value?.attributes?.seo?.seo_description ||
    'Digital Agency Indonesia',
  image:
    industryDetail?.value?.attributes?.seo?.image.data?.attributes?.url ||
    industryDetail.value.attributes?.banner?.background_image?.data?.attributes
      ?.url ||
    mainSeo.attributes?.seo?.image?.data?.attributes?.url ||
    '/icon.png'
})
useSeoMeta({
  title: industryDetail?.value?.attributes?.seo?.seo_title || 'Suitmedia',
  ogTitle: industryDetail?.value?.attributes?.seo?.seo_title || 'Suitmedia',
  description:
    industryDetail?.value?.attributes?.seo?.seo_description ||
    'Digital Agency Indonesia',
  ogDescription:
    industryDetail?.value?.attributes?.seo?.seo_description ||
    'Digital Agency Indonesia',
  ogImage:
    industryDetail?.value?.attributes?.seo?.image.data?.attributes?.url ||
    industryDetail.value.attributes?.banner?.background_image?.data?.attributes
      ?.url ||
    mainSeo.attributes?.seo?.image?.data?.attributes?.url ||
    '/icon.png',
  ogType: industryDetail?.value?.attributes?.seo?.open_graph_type,
  twitterTitle:
    industryDetail?.value?.attributes?.seo?.seo_title || 'Suitmedia',
  twitterDescription:
    industryDetail?.value?.attributes?.seo?.seo_description ||
    'Digital Agency Indonesia',
  twitterImage:
    industryDetail?.value?.attributes?.seo?.image.data?.attributes?.url ||
    industryDetail.value.attributes?.banner?.background_image?.data?.attributes
      ?.url ||
    mainSeo.attributes?.seo?.image?.data?.attributes?.url ||
    '/icon.png'
})

const bgImage = computed(() => {
  return industryDetail.value?.attributes?.banner?.background_image?.data
    ?.attributes?.url
})
</script>

<style lang="scss" scoped>
.icon {
  :deep(.nuxt-icon) {
    svg {
      width: 36px;
      height: 36px;
    }
  }
}

:deep(.content) {
  color: #353535;
  h1,
  h2 {
    font-size: 22px;
    line-height: normal;
    color: #353535;
    font-weight: 500 !important;

    margin-bottom: 32px;

    @media screen and (min-width: 640px) {
      font-size: 40px;
      line-height: 50px;
    }
  }

  p {
    margin-bottom: 20px;
  }

  ol {
    list-style: decimal;
    margin-left: 32px;
    margin-bottom: 20px;

    li {
      margin-bottom: 1rem;
    }
  }

  ul {
    list-style-type: disc;
    margin-left: 32px;
    margin-bottom: 20px;

    li {
      margin-bottom: 1rem;
    }
  }
}
</style>
